import React, { useState, useRef, useEffect, useCallback } from 'react';
import { InvitationDesign } from '../../_types/invitation-design-v2';
import StylesTab from './tabs/StylesTab';
import ColorsTab from './tabs/ColorsTab';
import BackgroundTab from './tabs/BackgroundTab';
import LayoutTab from './tabs/LayoutTab';

interface DesignerControlsProps {
  design: InvitationDesign;
  onStylePresetUpdate: (preset: string, updates: any) => void;
  onThemeUpdate: (updates: any) => void;
  onTemplateUpdate: (updates: any) => void;
}

type ControlPanel = 'styles' | 'theme' | 'background' | 'layout' | null;

interface PanelPosition {
  x: number;
  y: number;
}

const DesignerControls: React.FC<DesignerControlsProps> = ({
  design,
  onStylePresetUpdate,
  onThemeUpdate,
  onTemplateUpdate,
}) => {
  const [activePanel, setActivePanel] = useState<ControlPanel>(null);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const controlsRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);

  // Calculate initial position only if we don't have a saved position
  useEffect(() => {
    if (activePanel && controlsRef.current && !panelPosition) {
      // Calculate default position
      const controlsRect = controlsRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const panelWidth = 320; // w-80 = 20rem = 320px
      
      // Check if there's enough space on the right
      const spaceOnRight = windowWidth - controlsRect.right;
      
      let defaultPosition: PanelPosition;
      if (spaceOnRight >= panelWidth + 20) {
        // Position to the right of buttons
        defaultPosition = {
          x: controlsRect.right + 10,
          y: controlsRect.top + 16
        };
      } else {
        // Position to the left of buttons
        defaultPosition = {
          x: controlsRect.left - panelWidth - 10,
          y: controlsRect.top + 16
        };
      }
      
      setPanelPosition(defaultPosition);
    }
  }, [activePanel, panelPosition]);

  // Dragging handlers for both mouse and touch
  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    setIsDragging(true);
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: clientX - rect.left,
        y: clientY - rect.top
      });
    }
  }, []);

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    if (isDragging && panelRef.current) {
      const newPosition = {
        x: clientX - dragOffset.x,
        y: clientY - dragOffset.y
      };
      setPanelPosition(newPosition);
    }
  }, [isDragging, dragOffset]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Mouse event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (dragHandleRef.current && dragHandleRef.current.contains(e.target as Node)) {
      e.preventDefault();
      handleDragStart(e.clientX, e.clientY);
    }
  }, [handleDragStart]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    handleDragMove(e.clientX, e.clientY);
  }, [handleDragMove]);

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (dragHandleRef.current && dragHandleRef.current.contains(e.target as Node)) {
      const touch = e.touches[0];
      handleDragStart(touch.clientX, touch.clientY);
    }
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (isDragging) {
      e.preventDefault(); // Prevent scrolling while dragging
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    }
  }, [isDragging, handleDragMove]);

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Set up global mouse and touch event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      // Mouse events
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Touch events
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        // Clean up mouse events
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        
        // Clean up touch events
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (controlsRef.current && !controlsRef.current.contains(event.target as Node) && 
          panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setActivePanel(null);
      }
    };

    if (activePanel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activePanel]);

  const controls = [
    { key: 'styles' as const, icon: '📝', label: 'Styles' },
    { key: 'theme' as const, icon: '🎨', label: 'Theme' },
    { key: 'background' as const, icon: '🖼️', label: 'Background' },
    { key: 'layout' as const, icon: '📐', label: 'Layout' }
  ];

  return (
    <div className="absolute top-0 -right-11 h-full z-50" ref={controlsRef}>
      <div className="relative h-full flex flex-col gap-2 pt-4">
        {/* Control buttons - permanently visible */}
        {controls.map((control) => (
          <button
            key={control.key}
            onClick={() => {
              setActivePanel(activePanel === control.key ? null : control.key);
            }}
            className={`p-3 md:p-2 rounded-l-lg text-xl transition-all ${
              activePanel === control.key
                ? 'bg-green-500 text-white shadow-md'
                : 'bg-white shadow-md hover:shadow-lg'
            }`}
            title={control.label}
          >
            {control.icon}
          </button>
        ))}
      </div>

      {/* Draggable popup panels */}
      {activePanel && (
        <div 
          ref={panelRef}
          className="fixed w-80 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[500px] overflow-hidden z-[60]"
          style={{
            left: `${panelPosition?.x || 0}px`,
            top: `${panelPosition?.y || 0}px`,
            cursor: isDragging ? 'grabbing' : 'default'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div 
            ref={dragHandleRef}
            className="p-3 border-b border-gray-100 flex justify-between items-center cursor-grab hover:bg-gray-50 touch-none"
          >
            <h3 className="text-base font-semibold text-gray-800 select-none">
              {controls.find(c => c.key === activePanel)?.label}
            </h3>
            <button
              onClick={() => setActivePanel(null)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="overflow-y-auto max-h-[420px] p-3">
            {activePanel === 'styles' && (
              <StylesTab
                design={design}
                onStylePresetUpdate={onStylePresetUpdate}
                onThemeUpdate={onThemeUpdate}
              />
            )}
            
            {activePanel === 'theme' && (
              <ColorsTab
                design={design}
                onThemeUpdate={onThemeUpdate}
                onTemplateUpdate={onTemplateUpdate}
              />
            )}
            
            {activePanel === 'background' && (
              <BackgroundTab
                design={design}
                onTemplateUpdate={onTemplateUpdate}
              />
            )}
            
            {activePanel === 'layout' && (
              <LayoutTab
                design={design}
                onTemplateUpdate={onTemplateUpdate}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignerControls;